import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  RefreshCw,
  DollarSign,
  BarChart3,
} from "lucide-react";

const CryptoTradingDemo = () => {
  const [balance, setBalance] = useState(10000); // Mock balance in USD
  const [tokens, setTokens] = useState([
    { symbol: "ETH", name: "Ethereum", price: 1800, change: 2.5, balance: 0 },
    { symbol: "BTC", name: "Bitcoin", price: 45000, change: -1.2, balance: 0 },
    {
      symbol: "DEMO",
      name: "Demo Token",
      price: 0.5,
      change: 15.3,
      balance: 1000,
    },
    {
      symbol: "TEST",
      name: "Test Coin",
      price: 2.75,
      change: -3.8,
      balance: 500,
    },
  ]);

  const [selectedToken, setSelectedToken] = useState(tokens[0]);
  const [tradeAmount, setTradeAmount] = useState("");
  const [tradeType, setTradeType] = useState("buy");
  const [transactions, setTransactions] = useState([]);
  const [isTestnet, setIsTestnet] = useState(true);

  // Simulate price updates
  useEffect(() => {
    const interval = setInterval(() => {
      setTokens((prevTokens) =>
        prevTokens.map((token) => ({
          ...token,
          price: token.price * (1 + (Math.random() - 0.5) * 0.02), // ±1% random change
          change: token.change + (Math.random() - 0.5) * 2,
        }))
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const executeTrade = () => {
    const amount = parseFloat(tradeAmount);
    if (!amount || amount <= 0) return;

    const cost = amount * selectedToken.price;

    if (tradeType === "buy") {
      if (balance >= cost) {
        setBalance((prev) => prev - cost);
        setTokens((prev) =>
          prev.map((token) =>
            token.symbol === selectedToken.symbol
              ? { ...token, balance: token.balance + amount }
              : token
          )
        );

        const newTransaction = {
          id: Date.now(),
          type: "buy",
          token: selectedToken.symbol,
          amount: amount,
          price: selectedToken.price,
          total: cost,
          timestamp: new Date().toLocaleTimeString(),
        };
        setTransactions((prev) => [newTransaction, ...prev.slice(0, 9)]);
      }
    } else {
      if (selectedToken.balance >= amount) {
        setBalance((prev) => prev + cost);
        setTokens((prev) =>
          prev.map((token) =>
            token.symbol === selectedToken.symbol
              ? { ...token, balance: token.balance - amount }
              : token
          )
        );

        const newTransaction = {
          id: Date.now(),
          type: "sell",
          token: selectedToken.symbol,
          amount: amount,
          price: selectedToken.price,
          total: cost,
          timestamp: new Date().toLocaleTimeString(),
        };
        setTransactions((prev) => [newTransaction, ...prev.slice(0, 9)]);
      }
    }

    setTradeAmount("");
  };

  const resetDemo = () => {
    setBalance(10000);
    setTokens((prev) =>
      prev.map((token) => ({
        ...token,
        balance:
          token.symbol === "DEMO" ? 1000 : token.symbol === "TEST" ? 500 : 0,
      }))
    );
    setTransactions([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <BarChart3 className="h-8 w-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  Crypto Trading Demo
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isTestnet
                        ? "bg-green-500/20 text-green-400"
                        : "bg-red-500/20 text-red-400"
                    }`}
                  >
                    {isTestnet ? "TESTNET MODE" : "LIVE MODE"}
                  </div>
                  <button
                    onClick={() => setIsTestnet(!isTestnet)}
                    className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs hover:bg-blue-500/30 transition-colors"
                  >
                    Toggle Mode
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={resetDemo}
              className="flex items-center gap-2 px-4 py-2 bg-slate-600/50 text-white rounded-lg hover:bg-slate-600/70 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Reset Demo
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Portfolio */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-2 mb-6">
              <Wallet className="h-5 w-5 text-blue-400" />
              <h2 className="text-xl font-semibold text-white">Portfolio</h2>
            </div>

            <div className="mb-6">
              <div className="text-sm text-slate-300 mb-1">Total Balance</div>
              <div className="text-3xl font-bold text-white">
                ${balance.toFixed(2)}
              </div>
            </div>

            <div className="space-y-3">
              {tokens.map((token) => (
                <div
                  key={token.symbol}
                  className={`p-4 rounded-xl cursor-pointer transition-all ${
                    selectedToken.symbol === token.symbol
                      ? "bg-blue-500/20 border border-blue-400/50"
                      : "bg-white/5 hover:bg-white/10"
                  }`}
                  onClick={() => setSelectedToken(token)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold text-white">
                        {token.symbol}
                      </div>
                      <div className="text-sm text-slate-300">{token.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-white">
                        ${token.price.toFixed(4)}
                      </div>
                      <div
                        className={`text-sm flex items-center gap-1 ${
                          token.change >= 0 ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {token.change >= 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        {token.change.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  {token.balance > 0 && (
                    <div className="text-sm text-slate-300">
                      Balance: {token.balance.toFixed(4)} {token.symbol}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Trading Interface */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-2 mb-6">
              <DollarSign className="h-5 w-5 text-green-400" />
              <h2 className="text-xl font-semibold text-white">
                Trade {selectedToken.symbol}
              </h2>
            </div>

            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <div className="text-2xl font-bold text-white mb-1">
                ${selectedToken.price.toFixed(4)}
              </div>
              <div
                className={`text-sm flex items-center gap-1 ${
                  selectedToken.change >= 0 ? "text-green-400" : "text-red-400"
                }`}
              >
                {selectedToken.change >= 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {selectedToken.change.toFixed(2)}% (24h)
              </div>
            </div>

            <div className="mb-4">
              <div className="flex rounded-lg bg-white/5 p-1 mb-4">
                <button
                  className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                    tradeType === "buy"
                      ? "bg-green-500 text-white"
                      : "text-slate-300 hover:text-white"
                  }`}
                  onClick={() => setTradeType("buy")}
                >
                  Buy
                </button>
                <button
                  className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                    tradeType === "sell"
                      ? "bg-red-500 text-white"
                      : "text-slate-300 hover:text-white"
                  }`}
                  onClick={() => setTradeType("sell")}
                >
                  Sell
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm text-slate-300 mb-2">
                  Amount ({selectedToken.symbol})
                </label>
                <input
                  type="number"
                  value={tradeAmount}
                  onChange={(e) => setTradeAmount(e.target.value)}
                  className="w-full p-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-400"
                  placeholder="0.00"
                />
              </div>

              {tradeAmount && (
                <div className="mb-4 p-3 bg-white/5 rounded-lg">
                  <div className="text-sm text-slate-300">Total Cost</div>
                  <div className="text-lg font-semibold text-white">
                    $
                    {(
                      parseFloat(tradeAmount || 0) * selectedToken.price
                    ).toFixed(2)}
                  </div>
                </div>
              )}

              <button
                onClick={executeTrade}
                disabled={!tradeAmount || parseFloat(tradeAmount) <= 0}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                  tradeType === "buy"
                    ? "bg-green-500 hover:bg-green-600 text-white"
                    : "bg-red-500 hover:bg-red-600 text-white"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {tradeType === "buy" ? "Buy" : "Sell"} {selectedToken.symbol}
              </button>
            </div>
          </div>

          {/* Transaction History */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">
              Recent Transactions
            </h2>

            {transactions.length === 0 ? (
              <div className="text-center text-slate-400 py-8">
                No transactions yet
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div key={tx.id} className="p-3 bg-white/5 rounded-lg">
                    <div className="flex justify-between items-start mb-1">
                      <span
                        className={`font-semibold ${
                          tx.type === "buy" ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {tx.type.toUpperCase()} {tx.token}
                      </span>
                      <span className="text-sm text-slate-300">
                        {tx.timestamp}
                      </span>
                    </div>
                    <div className="text-sm text-slate-300">
                      {tx.amount.toFixed(4)} {tx.token} @ ${tx.price.toFixed(4)}
                    </div>
                    <div className="text-sm font-semibold text-white">
                      Total: ${tx.total.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Testnet Instructions */}
        {isTestnet && (
          <div className="mt-6 bg-green-500/10 border border-green-500/20 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-green-400 mb-3">
              Testnet Development Guide
            </h3>
            <div className="text-sm text-slate-300 space-y-2">
              <p>
                <strong>For Ethereum Testnet:</strong>
              </p>
              <p>• Use Sepolia testnet with MetaMask</p>
              <p>• Get free ETH from Sepolia faucet</p>
              <p>• Deploy contracts using Hardhat/Remix</p>
              <p>
                <strong>Integration:</strong> Replace mock data with
                Web3.js/Ethers.js calls to your smart contracts
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoTradingDemo;
